import React, { ReactNode, useState, useContext, useMemo } from 'react';
import { Box, Button, Chip, CircularProgress, Fade, IconButton, Paper, Stack, styled, Typography } from '@mui/material';
import { Close, DoneAll } from '@mui/icons-material';
import { TaskState } from '../../../interfaces/common/async_tasks';
import AsyncTaskProgressBar from '../async-task-progress-bar/async-task-progress-bar';
import axios from 'axios';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { useSnackBar } from '../../../contexts/snackbar';
import { AuthContext } from '../../../contexts/user-context/user-context';

interface TaskCardProps {
  status: TaskState;
  borderPosition?: 'left' | 'right';
}

const TaskCard = styled(Paper)<TaskCardProps>(({ theme, status, borderPosition = 'left' }) => ({
  padding: theme.spacing(2),
  width: '100%',
  background: theme.palette.background.paper,
  [`border${borderPosition.charAt(0).toUpperCase() + borderPosition.slice(1)}`]: '6px solid',
  borderColor: theme.palette[status === 'FAILED'
    ? 'error'
    : status === 'COMPLETED'
    ? 'success'
    : 'primary'].main,
  position: 'relative',
  overflow: 'hidden',
  marginBottom: theme.spacing(2),
}));

export interface BaseTask {
  task_id: string;
  status: TaskState;
  progress: number;
  created_at: string;
  result?: Record<string, unknown>;
}

interface AsyncTaskListProps<T extends BaseTask> {
  tasks: T[];
  taskLabel: string;
  taskIcon: ReactNode;
  chipColor?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  progressColor?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  processingText?: string;
  borderPosition?: 'left' | 'right';
  dismissButtonColor?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  renderTaskStats?: (task: T) => ReactNode;
  showSpinnerWhenStarted?: boolean;
  onTasksUpdated?: (tasks: T[]) => void;
  minimal?: boolean;
  compact?: boolean;
}

function AsyncTaskList<T extends BaseTask>({
  tasks,
  taskLabel,
  taskIcon,
  chipColor = 'primary',
  progressColor,
  processingText = 'Processing...',
  borderPosition = 'left',
  dismissButtonColor,
  renderTaskStats,
  showSpinnerWhenStarted = true,
  onTasksUpdated,
  minimal = false,
  compact = false,
}: AsyncTaskListProps<T>) {
  const { config } = useContext(AuthContext);
  const { showMessage } = useSnackBar();
  const [dismissingTasks, setDismissingTasks] = useState<Set<string>>(new Set());

  const hasCompletedTasks = tasks.some(
    task => task.status === TaskState.COMPLETED || task.status === TaskState.FAILED
  );

  const dismissTask = async (taskId: string) => {
    setDismissingTasks(prev => new Set(prev).add(taskId));
    try {
      await axios.post(
        `${API_ENDPOINT}/v1/jobs/dismiss/`,
        { task_ids: [taskId] },
        config
      );
      const updatedTasks = tasks.filter(task => task.task_id !== taskId);
      if (onTasksUpdated) {
        onTasksUpdated(updatedTasks);
      }
      showMessage('Task dismissed');
    } catch (error) {
      console.error('Failed to dismiss task:', error);
      showMessage('Failed to dismiss task', 'error');
    } finally {
      setDismissingTasks(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskId);
        return newSet;
      });
    }
  };

  const dismissAllCompleted = async () => {
    const completedTaskIds = tasks
      .filter(task => task.status === TaskState.COMPLETED || task.status === TaskState.FAILED)
      .map(task => task.task_id);

    if (completedTaskIds.length === 0) {
      showMessage('No completed tasks to dismiss');
      return;
    }

    setDismissingTasks(new Set(completedTaskIds));

    try {
      await axios.post(
        `${API_ENDPOINT}/v1/jobs/dismiss/`,
        { task_ids: completedTaskIds },
        config
      );
      const updatedTasks = tasks.filter(
        task => !completedTaskIds.includes(task.task_id)
      );
      if (onTasksUpdated) {
        onTasksUpdated(updatedTasks);
      }
      showMessage(`Dismissed ${completedTaskIds.length} task${completedTaskIds.length > 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Failed to dismiss tasks:', error);
      showMessage('Failed to dismiss tasks', 'error');
    } finally {
      setDismissingTasks(new Set());
    }
  };

  if (tasks.length === 0) {
    return null;
  }

  const renderTaskCards = (): React.ReactNode=>{

    if (minimal) {
      return (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
          {tasks.map((task) => (
            <Fade in={true} key={task.task_id}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
     
                  }}
                >
                  <CircularProgress size={20} />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {task.progress}%
                </Typography>
              </Box>
            </Fade>
          ))}
        </Box>
      );
    }

    if (compact) {
      return (
        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: '500px', margin: 'auto' }}>
          {tasks.map((task) => (
            <Fade in={true} key={task.task_id}>
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  width: '100%',
                  borderRadius: 2,
                  background: (theme) => theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f8f9fa',
                  mb: 2
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        color: progressColor ? `${progressColor}.main` : 'primary.main'
                      }}
                    >
                      <CircularProgress size={20} />
                    </Box>
                    <Typography variant="body2" fontWeight={600} color={progressColor}>
                      {taskLabel}
                    </Typography>
                  </Box>
                  <Chip
                    label={`${task.progress}%`}
                    size="small"
                    color={progressColor}
                    sx={{ fontWeight: 600, minWidth: 60 }}
                  />
                </Box>
  
                {renderTaskStats && (
                  <Box sx={{ mb: 1.5 }}>
                    {renderTaskStats(task)}
                  </Box>
                )}
  
                <AsyncTaskProgressBar
                  progress={task.progress}
                  status={task.status}
                  color={progressColor}
                  processingText={processingText}
                />
              </Paper>
            </Fade>
          ))}
        </Box>
      );
    }

    return <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: '500px', margin: 'auto' }}>
      { tasks.map((task) => (
      <Fade in={true} key={task.task_id}>
        <TaskCard elevation={3} status={task.status} borderPosition={borderPosition}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <Chip
                icon={<>{taskIcon}</>}
                label={taskLabel}
                size="small"
                color={chipColor}
                sx={{ fontWeight: 600 }}
              />
              <Typography variant="caption" color="text.secondary">
                Task ID: {task.task_id.slice(0, 8)}... • {new Date(task.created_at).toLocaleTimeString()}
              </Typography>
              {showSpinnerWhenStarted && task.status === TaskState.STARTED && (
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
          
                  }}
                >
                  <CircularProgress size={20} />
                </Box>
              )}
            </Box>

            {(task.status === TaskState.COMPLETED || task.status === TaskState.FAILED) && (
              <>
                {dismissingTasks.has(task.task_id) ? (
                  <CircularProgress size={20} />
                ) : (
                  <IconButton onClick={() => dismissTask(task.task_id)} size="small">
                    <Close />
                  </IconButton>
                )}
              </>
            )}
          </Box>

          {renderTaskStats && (
            <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
              {renderTaskStats(task)}
            </Box>
          )}

          <AsyncTaskProgressBar
            progress={task.progress}
            status={task.status}
            color={progressColor}
            processingText={processingText}
          />
        </TaskCard>
      </Fade>))}
      </Box>
    

    
    
  }

  

 

  return (
    <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: '600px', margin: 'auto' }}>
        {hasCompletedTasks && (
          <Box><Button
            variant="outlined"
            color={dismissButtonColor}
            startIcon={<DoneAll />}
            onClick={dismissAllCompleted}
            disabled={dismissingTasks.size > 0}
            sx={{ ml: 'auto', mb: 2 }}
          >
            Dismiss All Completed
          </Button>
          </Box>
        )}
        
        {renderTaskCards()}

      
    </Box>
  );
}

export default AsyncTaskList;
